import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { login, AuthError } from '../lib/auth-api';
import { useAuth } from '../contexts/AuthContext';
import OAuthButtons from './OAuthButtons';

export default function LoginPage() {
  const navigate = useNavigate();
  const { setAuthenticated } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setNeedsVerification(false);

    if (!email || !password) {
      setError('Veuillez remplir tous les champs.');
      return;
    }

    setLoading(true);
    try {
      const data = await login(email, password);

      // Decode user info from the JWT
      const parts = data.access_token.split('.');
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      setAuthenticated({ id: payload.sub, email: payload.email });

      navigate('/saisie', { replace: true });
    } catch (err: unknown) {
      if (err instanceof AuthError) {
        setError(err.message);
        if (err.data?.requires_verification) {
          setNeedsVerification(true);
        }
      } else if (err instanceof Error) {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-sm w-full">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Se connecter</h1>
          <p className="text-sm text-gray-500 mt-1">Accédez à votre comptabilité</p>
        </div>

        {error && (
          <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">
            {error}
          </div>
        )}

        {needsVerification && (
          <div className="text-blue-700 bg-blue-50 border border-blue-200 px-4 py-3 rounded-lg text-sm mb-4">
            Vérifiez votre boîte mail pour le code de vérification.{' '}
            <Link to={`/verify-email?email=${encodeURIComponent(email)}`} className="underline">
              Entrer le code
            </Link>
          </div>
        )}

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Adresse e-mail</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="vous@exemple.fr"
            />
          </div>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Mot de passe</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
          <OAuthButtons />
        </form>

        <div className="mt-4 text-center text-sm space-y-2">
          <div>
            <Link to="/reset-password" className="text-blue-600 hover:underline">Mot de passe oublié ?</Link>
          </div>
          <div className="text-gray-500">
            Pas encore de compte ?{' '}
            <Link to="/signup" className="text-blue-600 hover:underline">Créer un compte</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
